import axios from 'axios'

type TokenData = {
  accessToken: string | null
  refreshToken: string | null
}

type EventHandlers = {
  onTokenCleared?: () => void
  onTokenNotFound?: () => void
  onTokenReceived?: () => void
}

type Environment = 'dev' | 'stg' | 'live' | 'custom'

const getBaseURL = (env: Environment, customURL?: string): string => {
  switch (env) {
    case 'live':
      return 'https://api.scienceflat.com'
    case 'stg':
      return 'https://api-staging.scienceflat.com'
    case 'dev':
      return 'https://api-dev.scienceflat.com'
    case 'custom':
      if (customURL === 'dev') return 'https://api-dev.scienceflat.com'
      if (!customURL) {
        throw new Error('Custom URL is required when environment is "custom"')
      }
      return customURL
  }
}
const TOKEN_KEYS = {
  accessToken: 'accessToken',
  refreshToken: 'refreshToken',
} as const

/**
 * JWT 토큰 관리 및 인증 서비스
 *
 * - 토큰 저장/조회/재발급
 * - 로그인/로그아웃
 *
 * Teacher, Student 앱 모두 동일한 인스턴스를 사용합니다.
 */

class JwtTokenService {
  private _tokenData: TokenData = {
    accessToken: null,
    refreshToken: null,
  }

  private broadcastChannel = new BroadcastChannel('scienceflat-jwt-token')

  private baseURL: string | null = null
  private platform: string = 'TEACHER_WEB'
  private eventHandlers: EventHandlers = {}

  currentStorage: typeof localStorage | typeof sessionStorage = sessionStorage

  get tokenStatus(): 'VALID' | 'INVALID' {
    const { accessToken } = this._tokenData

    if (!accessToken) return 'INVALID'
    return 'VALID'
  }

  get isLoggedIn(): boolean {
    const { accessToken, refreshToken } = this._tokenData
    return accessToken !== null && refreshToken !== null
  }

  get isUsingLocalStorage(): boolean {
    return this.currentStorage === localStorage
  }

  constructor() {
    // 토큰 로드 (localStorage 우선, 없으면 sessionStorage)
    this._tokenData = {
      accessToken:
        localStorage.getItem(TOKEN_KEYS.accessToken) ||
        sessionStorage.getItem(TOKEN_KEYS.accessToken),
      refreshToken:
        localStorage.getItem(TOKEN_KEYS.refreshToken) ||
        sessionStorage.getItem(TOKEN_KEYS.refreshToken),
    }

    // localStorage에 토큰이 있으면 localStorage 사용
    this.currentStorage = localStorage.getItem(TOKEN_KEYS.accessToken)
      ? localStorage
      : sessionStorage

    this._setupBroadcast()
  }

  /**
   * 환경에 따른 baseURL 설정 (앱 초기화 시 호출)
   *
   * @param env - 환경 타입 ('dev' | 'stg' | 'live' | 'custom')
   * @param customURL - env가 'custom'일 때 사용할 URL
   *
   * @example
   * jwtTokenService.setEnvironment('dev')
   * jwtTokenService.setEnvironment('live')
   * jwtTokenService.setEnvironment('custom', 'https://custom-api.example.com')
   */
  setEnvironment(env: Environment, customURL?: string): void {
    this.baseURL = getBaseURL(env, customURL)
  }

  setPlatform(platform: string): void {
    this.platform = platform
  }

  /**
   * 이벤트 핸들러 설정 (앱 레이어에서 redirect/reload 동작 구현)
   *
   * @param handlers - 이벤트 핸들러 객체
   *
   * @example
   * jwtTokenService.setEventHandlers({
   *   onTokenCleared: () => window.location.replace('/login'),
   *   onTokenNotFound: () => window.location.replace('/login'),
   *   onTokenReceived: () => window.location.reload(),
   * })
   */
  setEventHandlers(handlers: EventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers }
  }

  /**
   * baseURL 체크 및 에러 처리
   * @throws baseURL이 설정되지 않은 경우 에러 발생
   */
  private ensureBaseURL(): string {
    if (!this.baseURL) {
      throw new Error(
        'baseURL is not set. Please call setEnvironment() before making API requests.',
      )
    }
    return this.baseURL
  }

  /**
   * localStorage 사용 모드로 전환 (로그인 상태 유지)
   * 이미 sessionStorage에 토큰이 있다면 localStorage로 이동시킵니다
   */
  useLocalStorage(): void {
    // 1. localStorage 사용으로 전환
    this.currentStorage = localStorage

    // 2. 이미 sessionStorage에 토큰이 있다면 localStorage로 이동
    const sessionAccessToken = sessionStorage.getItem(TOKEN_KEYS.accessToken)
    const sessionRefreshToken = sessionStorage.getItem(TOKEN_KEYS.refreshToken)

    if (sessionAccessToken) {
      localStorage.setItem(TOKEN_KEYS.accessToken, sessionAccessToken)
      sessionStorage.removeItem(TOKEN_KEYS.accessToken)
    }
    if (sessionRefreshToken) {
      localStorage.setItem(TOKEN_KEYS.refreshToken, sessionRefreshToken)
      sessionStorage.removeItem(TOKEN_KEYS.refreshToken)
    }
  }

  getAccessToken(): string | null {
    return this._tokenData.accessToken
  }

  getRefreshToken(): string | null {
    return this._tokenData.refreshToken
  }

  /**
   * 새로운 토큰을 저장하고 다른 탭에 브로드캐스트합니다
   *
   * @param updates - 업데이트할 토큰 데이터
   * @param broadcastType - 브로드캐스트 타입 ('LOGIN' | 'TOKEN_UPDATED' | null)
   *   - 'LOGIN': 로그인 시 (다른 탭들도 reload)
   *   - 'TOKEN_UPDATED': 토큰 재발급 시
   *   - null: 브로드캐스트 하지 않음
   */
  setTokens(
    updates: Partial<TokenData>,
    broadcastType: 'LOGIN' | 'TOKEN_UPDATED' | null = 'TOKEN_UPDATED',
  ): void {
    this._tokenData = { ...this._tokenData, ...updates }

    // storage에 저장 또는 삭제
    // null/undefined는 명시적 삭제 의도, truthy만 저장
    if ('accessToken' in updates) {
      if (updates.accessToken) {
        this.currentStorage.setItem(TOKEN_KEYS.accessToken, updates.accessToken)
      } else {
        this.currentStorage.removeItem(TOKEN_KEYS.accessToken)
      }
    }
    if ('refreshToken' in updates) {
      if (updates.refreshToken) {
        this.currentStorage.setItem(TOKEN_KEYS.refreshToken, updates.refreshToken)
      } else {
        this.currentStorage.removeItem(TOKEN_KEYS.refreshToken)
      }
    }
    if (broadcastType) {
      this.broadcastChannel.postMessage({
        message: broadcastType,
        payload: this._tokenData,
      })
    }
  }

  /**
   * 토큰을 삭제하고 다른 탭에 브로드캐스트합니다
   * localStorage와 sessionStorage 양쪽 모두에서 제거합니다
   */
  clearTokens(shouldBroadcast = true): void {
    this._tokenData = { accessToken: null, refreshToken: null }
    this.currentStorage = sessionStorage

    // 양쪽 storage 모두 제거
    localStorage.removeItem(TOKEN_KEYS.accessToken)
    localStorage.removeItem(TOKEN_KEYS.refreshToken)
    sessionStorage.removeItem(TOKEN_KEYS.accessToken)
    sessionStorage.removeItem(TOKEN_KEYS.refreshToken)

    if (shouldBroadcast) {
      this.broadcastChannel.postMessage({ message: 'TOKEN_CLEARED' })
    }
  }

  requestToken() {
    this.broadcastChannel.postMessage({ message: 'REQUEST_TOKEN' })
  }

  /**
   * 토큰 재발급
   */
  reissueTokens = async () => {
    const baseURL = this.ensureBaseURL()

    const {
      data: { accessToken, refreshToken },
    } = await axios.post<{ accessToken: string; refreshToken: string }>(
      // [변경] /v2/reissue → /auth/reissue (새 프로젝트는 JWT 전용)
      `${baseURL}/auth/reissue`,
      { refreshToken: this._tokenData.refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-platform': this.platform,
        },
      },
    )

    this.setTokens({
      accessToken,
      refreshToken,
    })

    return { accessToken, refreshToken }
  }

  /**
   * JWT 로그인 응답 처리
   */
  processJwtLoginResponse = async ({
    accessToken,
    refreshToken,
  }: {
    accessToken: string
    refreshToken: string
  }) => {
    // 로그인 시에는 'LOGIN' 메시지로 브로드캐스트
    this.setTokens(
      {
        accessToken,
        refreshToken,
      },
      'LOGIN',
    )
  }

  /**
   * 앱 초기화 시 인증 상태 체크
   *
   * Note: 토큰 만료 여부는 서버에서 검증합니다 (401 응답 시 자동 재발급)
   *
   * @returns 인증된 상태인지 여부
   */
  initializeAuth = async (): Promise<boolean> => {
    const { accessToken } = this._tokenData

    // 아무 토큰도 없으면 다른 탭에 토큰 요청
    if (!accessToken) {
      this.requestToken()
    }

    return this.isLoggedIn
  }

  private _setupBroadcast(): void {
    this.broadcastChannel.onmessage = (event: MessageEvent) => {
      const data = event.data

      switch (data.message) {
        case 'LOGIN':
          // 다른 탭에서 로그인함
          this.setTokens(data.payload, null)
          this.eventHandlers.onTokenReceived?.()
          break

        case 'TOKEN_UPDATED':
          // 다른 탭에서 토큰 재발급
          this.setTokens(data.payload, null)
          break

        case 'REQUEST_TOKEN':
          // 다른 탭이 토큰 요청
          // 토큰 있으면 TOKEN으로 전송, 없으면 TOKEN_NOT_FOUND로 전송
          if (this._tokenData.accessToken) {
            this.broadcastChannel.postMessage({
              message: 'TOKEN',
              payload: { token: this._tokenData },
            })
          } else {
            this.broadcastChannel.postMessage({ message: 'TOKEN_NOT_FOUND' })
          }
          break

        case 'TOKEN':
          // 내가 요청한 토큰 응답 받음
          this.setTokens({ ...data.payload.token }, null)
          this.eventHandlers.onTokenReceived?.()
          break

        case 'TOKEN_NOT_FOUND':
          // 다른 탭도 토큰 없음
          this.eventHandlers.onTokenNotFound?.()
          break

        case 'TOKEN_CLEARED':
          // 다른 탭에서 로그아웃함
          this.clearTokens(false)
          this.eventHandlers.onTokenCleared?.()
          break
      }
    }
  }
}

export { JwtTokenService }
export const jwtTokenService = new JwtTokenService()
